import { useState, useEffect, useMemo } from 'react';
import { X, Plus, Search, Minus } from 'lucide-react';
import { indianFoodService, IndianFoodItem } from '../services/indian-food.service';

function getCategoryDisplayName(category: string): string {
  const names: Record<string, string> = {
    breakfast: 'Breakfast',
    main_course: 'Main Course',
    curry: 'Curries & Gravies',
    side_dish: 'Side Dishes',
    snacks: 'Snacks',
    dessert: 'Desserts',
  };
  return names[category] || category;
}

/** Get macro value from API item - supports both camelCase and snake_case */
function getMacro(item: any, camelKey: string): number {
  const snake = camelKey.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
  const v = item?.[camelKey] ?? (item as any)?.[snake];
  return typeof v === 'number' && !isNaN(v) ? v : 0;
}

export interface FoodEntry {
  id: string;
  name: string;
  quantity: number;
  calories: number;
  carbs: number;
  protein: number;
  fat: number;
  fiber?: number;
  source: 'database_select' | 'manual_entry' | 'ai_detected';
  item?: IndianFoodItem;
}

interface ManualFoodEntryProps {
  onClose: () => void;
  initialEntries?: Array<{
    name: string;
    quantity?: number;
    calories: number;
    carbs?: number;
    protein?: number;
    fat?: number;
  }>;
  onSave: (
    entries: FoodEntry[],
    totalCalories: number,
    totalCarbs: number,
    totalProtein: number,
    totalFat: number
  ) => void;
}

export function ManualFoodEntry({ onClose, initialEntries = [], onSave }: ManualFoodEntryProps) {
  const [filteredItems, setFilteredItems] = useState<IndianFoodItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [entries, setEntries] = useState<FoodEntry[]>([]);
  const [showCustomEntry, setShowCustomEntry] = useState(false);
  const [customFood, setCustomFood] = useState({
    name: '',
    quantity: 100,
    calories: 0,
    carbs: 0,
    protein: 0,
    fat: 0,
  });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const categories = useMemo(
    () => ['all', 'breakfast', 'main_course', 'curry', 'side_dish', 'snacks', 'dessert'],
    []
  );

  useEffect(() => {
    if (initialEntries.length === 0) {
      setEntries([]);
      return;
    }

    setEntries(
      initialEntries.map((entry, index) => ({
        id: `initial-${index}-${entry.name}`,
        name: entry.name,
        quantity: entry.quantity ?? 100,
        calories: Math.round(entry.calories),
        carbs: entry.carbs ?? 0,
        protein: entry.protein ?? 0,
        fat: entry.fat ?? 0,
        source: 'ai_detected' as const,
      }))
    );
  }, [initialEntries]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    indianFoodService
      .search({
        category: selectedCategory === 'all' ? undefined : selectedCategory,
        q: searchQuery || undefined,
        limit: 50,
      })
      .then((data) => {
        if (!cancelled) setFilteredItems(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setFilteredItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [searchQuery, selectedCategory]);

  const addFoodItem = (
    item: IndianFoodItem,
    quantity: number = getMacro(item, 'typicalServingSize') || 100
  ) => {
    const multiplier = quantity / 100;
    const cal = getMacro(item, 'caloriesPer100g');
    const carbs = getMacro(item, 'carbsPer100g');
    const protein = getMacro(item, 'proteinPer100g');
    const fat = getMacro(item, 'fatPer100g');
    const fiber = getMacro(item, 'fiberPer100g');
    const entry: FoodEntry = {
      id: Math.random().toString(),
      name: item.name,
      quantity,
      calories: Math.round(cal * multiplier),
      carbs: Math.round(carbs * multiplier * 10) / 10,
      protein: Math.round(protein * multiplier * 10) / 10,
      fat: Math.round(fat * multiplier * 10) / 10,
      fiber: fiber ? Math.round(fiber * multiplier * 10) / 10 : undefined,
      source: 'database_select',
      item,
    };
    setEntries((prev) => [...prev, entry]);
  };

  const addCustomFood = () => {
    if (!customFood.name || customFood.calories === 0) return;

    const entry: FoodEntry = {
      id: Math.random().toString(),
      name: customFood.name,
      quantity: customFood.quantity,
      calories: customFood.calories,
      carbs: customFood.carbs,
      protein: customFood.protein,
      fat: customFood.fat,
      source: 'manual_entry',
    };

    setEntries((prev) => [...prev, entry]);
    setCustomFood({ name: '', quantity: 100, calories: 0, carbs: 0, protein: 0, fat: 0 });
    setShowCustomEntry(false);
  };

  const removeEntry = (id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const updateQuantity = (id: string, newQuantity: number) => {
    setEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== id) return entry;
        const item = entry.item;
        if (!item || entry.source === 'manual_entry') return entry;

        const multiplier = newQuantity / 100;
        const cal = getMacro(item, 'caloriesPer100g');
        const carbs = getMacro(item, 'carbsPer100g');
        const protein = getMacro(item, 'proteinPer100g');
        const fat = getMacro(item, 'fatPer100g');
        const fiber = getMacro(item, 'fiberPer100g');
        return {
          ...entry,
          quantity: newQuantity,
          calories: Math.round(cal * multiplier),
          carbs: Math.round(carbs * multiplier * 10) / 10,
          protein: Math.round(protein * multiplier * 10) / 10,
          fat: Math.round(fat * multiplier * 10) / 10,
          fiber: fiber ? Math.round(fiber * multiplier * 10) / 10 : undefined,
        };
      })
    );
  };

  const updateEntryField = (
    id: string,
    field: 'name' | 'calories' | 'carbs' | 'protein' | 'fat',
    value: string
  ) => {
    setEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== id) return entry;

        if (field === 'name') {
          return { ...entry, name: value };
        }

        const parsedValue = Number(value);
        return {
          ...entry,
          [field]: Number.isFinite(parsedValue) ? parsedValue : 0,
        };
      })
    );
  };

  const calculateTotals = () => {
    return entries.reduce(
      (acc, entry) => ({
        calories: acc.calories + entry.calories,
        carbs: acc.carbs + entry.carbs,
        protein: acc.protein + entry.protein,
        fat: acc.fat + entry.fat,
      }),
      { calories: 0, carbs: 0, protein: 0, fat: 0 }
    );
  };

  const handleSave = () => {
    const totals = calculateTotals();
    onSave(entries, totals.calories, totals.carbs, totals.protein, totals.fat);
  };

  const totals = calculateTotals();

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {initialEntries.length > 0 ? 'Review AI Food Detection' : 'Add Food Manually'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-4">
              <div className="flex-1 relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search Indian foods..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-dropsugar-green focus:outline-none"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowCustomEntry(!showCustomEntry)}
                className="px-4 py-3 bg-dropsugar-green text-white rounded-xl hover:bg-dropsugar-green-dark transition-colors font-medium flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                <span>Custom</span>
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedCategory === cat ? 'bg-dropsugar-green text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat === 'all' ? 'All' : getCategoryDisplayName(cat)}
                </button>
              ))}
            </div>
          </div>

          {showCustomEntry && (
            <div className="bg-dropsugar-green/10 rounded-xl p-6 mb-6 border-2 border-dropsugar-green/30">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Add Custom Food Item</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Food Name</label>
                  <input
                    type="text"
                    value={customFood.name}
                    onChange={(e) => setCustomFood({ ...customFood, name: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-dropsugar-green focus:outline-none"
                    placeholder="Enter food name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Quantity (g)</label>
                  <input
                    type="number"
                    value={customFood.quantity}
                    onChange={(e) =>
                      setCustomFood({ ...customFood, quantity: Number(e.target.value) })
                    }
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-dropsugar-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Calories</label>
                  <input
                    type="number"
                    value={customFood.calories}
                    onChange={(e) =>
                      setCustomFood({ ...customFood, calories: Number(e.target.value) })
                    }
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-dropsugar-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Carbs (g)</label>
                  <input
                    type="number"
                    value={customFood.carbs}
                    onChange={(e) =>
                      setCustomFood({ ...customFood, carbs: Number(e.target.value) })
                    }
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-dropsugar-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Protein (g)</label>
                  <input
                    type="number"
                    value={customFood.protein}
                    onChange={(e) =>
                      setCustomFood({ ...customFood, protein: Number(e.target.value) })
                    }
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-dropsugar-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fat (g)</label>
                  <input
                    type="number"
                    value={customFood.fat}
                    onChange={(e) =>
                      setCustomFood({ ...customFood, fat: Number(e.target.value) })
                    }
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-dropsugar-green focus:outline-none"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={addCustomFood}
                className="mt-4 w-full px-4 py-3 bg-dropsugar-green text-white rounded-lg hover:bg-dropsugar-green-dark transition-colors font-medium"
              >
                Add to Meal
              </button>
            </div>
          )}

          {loading && (
            <div className="mb-6 text-center py-8 text-gray-500">Loading foods...</div>
          )}

          {!loading && loadError && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
              <p className="font-medium">{loadError}</p>
              <p className="text-sm mt-1">Run <code className="bg-amber-100 px-1 rounded">npx prisma db seed</code> in the backend folder if the database is empty.</p>
            </div>
          )}

          {!loading && !loadError && filteredItems.length === 0 && !showCustomEntry && (
            <div className="mb-6 text-center py-8 text-gray-500">
              No Indian foods found. Try a different category or search, or add a custom food item.
            </div>
          )}

          {!loading && filteredItems.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Available Foods</h3>
              <div className="grid gap-3">
                {filteredItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors border border-gray-200"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">{item.name}</h4>
                        {item.nameTamil && (
                          <p className="text-sm text-gray-600">{item.nameTamil}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {getMacro(item, 'caloriesPer100g')} cal | {getMacro(item, 'carbsPer100g')}g carbs | Typical:{' '}
                          {getMacro(item, 'typicalServingSize') || 100}g
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => addFoodItem(item)}
                        className="p-2 bg-dropsugar-green text-white rounded-lg hover:bg-dropsugar-green-dark transition-colors"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {entries.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Your Meal</h3>
              <div className="space-y-3">
                {entries.map((entry) => (
                  <div
                    key={entry.id}
                    className="bg-white rounded-lg p-4 border-2 border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">{entry.name}</h4>
                      <button
                        type="button"
                        onClick={() => removeEntry(entry.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center space-x-4 mb-2">
                      <label className="text-sm text-gray-600">Quantity (g):</label>
                      <input
                        type="number"
                        value={entry.quantity}
                        onChange={(e) =>
                          updateQuantity(entry.id, Number(e.target.value))
                        }
                        className="w-24 px-3 py-1 border border-gray-300 rounded focus:border-dropsugar-green focus:outline-none"
                        disabled={entry.source === 'manual_entry'}
                      />
                    </div>
                    {entry.source === 'database_select' ? (
                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <div>
                          <p className="text-gray-600">Calories</p>
                          <p className="font-semibold">{entry.calories}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Carbs</p>
                          <p className="font-semibold">{entry.carbs}g</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Protein</p>
                          <p className="font-semibold">{entry.protein}g</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Fat</p>
                          <p className="font-semibold">{entry.fat}g</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                        <div className="md:col-span-1">
                          <label className="block text-gray-600 mb-1">Food</label>
                          <input
                            type="text"
                            value={entry.name}
                            onChange={(e) => updateEntryField(entry.id, 'name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-dropsugar-green focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-600 mb-1">Calories</label>
                          <input
                            type="number"
                            value={entry.calories}
                            onChange={(e) => updateEntryField(entry.id, 'calories', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-dropsugar-green focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-600 mb-1">Carbs</label>
                          <input
                            type="number"
                            value={entry.carbs}
                            onChange={(e) => updateEntryField(entry.id, 'carbs', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-dropsugar-green focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-600 mb-1">Protein</label>
                          <input
                            type="number"
                            value={entry.protein}
                            onChange={(e) => updateEntryField(entry.id, 'protein', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-dropsugar-green focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-600 mb-1">Fat</label>
                          <input
                            type="number"
                            value={entry.fat}
                            onChange={(e) => updateEntryField(entry.id, 'fat', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-dropsugar-green focus:outline-none"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Total Calories</p>
              <p className="text-2xl font-bold text-gray-900">{totals.calories}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Carbs</p>
              <p className="text-2xl font-bold text-gray-900">{totals.carbs.toFixed(1)}g</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Protein</p>
              <p className="text-2xl font-bold text-gray-900">{totals.protein.toFixed(1)}g</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Fat</p>
              <p className="text-2xl font-bold text-gray-900">{totals.fat.toFixed(1)}g</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={entries.length === 0}
              className="flex-1 px-6 py-3 bg-dropsugar-green text-white rounded-xl hover:bg-dropsugar-green-dark transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Save Meal Log
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
